import { useEffect, useState, useCallback } from 'react' 
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import PhotoLightbox from '../components/PhotoLightbox'

const TIER_COLORS = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', border: '#f09595', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', border: '#EF9F27', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', border: '#97C459', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', border: '#D3D1C7', dot: '⚪' },
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (mins < 60)  return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

function InfoRow({ label, value, valueColor }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: valueColor || 'var(--text)' }}>{value || '—'}</div>
    </div>
  )
}

function SiteInfoPanel({ details }) {
  if (!details) return null
  const tier = TIER_COLORS[details.priority_tier] || TIER_COLORS['Low']
  return (
    <div style={{ background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: tier.color }}>{tier.dot} {details.priority_tier} Priority</div>
        <div style={{ fontSize: 12, color: tier.color }}>Score: {details.priority_score ?? '—'}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
        <InfoRow label="Site code"       value={details.site_code} />
        <InfoRow label="District"        value={details.district} />
        <InfoRow label="Camera location" value={details.camera_location} />
        <InfoRow label="Manual LP entry" value={details.pct_lp_manual != null ? `${(details.pct_lp_manual * 100).toFixed(1)}%` : '—'} />
        <InfoRow label="LPR issue"  value={details.lpr_issue === true ? 'Yes' : details.lpr_issue === false ? 'No' : '—'} valueColor={details.lpr_issue === true ? '#A32D2D' : details.lpr_issue === false ? '#3B6D11' : undefined} />
        <InfoRow label="Gate issue" value={details.gate_issue === true ? 'Yes' : details.gate_issue === false ? 'No' : '—'} valueColor={details.gate_issue === true ? '#A32D2D' : details.gate_issue === false ? '#3B6D11' : undefined} />
      </div>
    </div>
  )
}

function LaneImagesPanel({ lanes }) {
  const [selectedLane, setSelectedLane] = useState(null)
  if (!lanes || lanes.length === 0) return null

  const kiosks = lanes.filter(l => l.lane_type === 'Kiosk')
  const pos    = lanes.filter(l => l.lane_type === 'Pos')

  function LaneCard({ lane }) {
    const ago = lane.captured_at ? timeAgo(lane.captured_at) : null
    return (
      <div onClick={() => setSelectedLane(lane)} style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
        overflow: 'hidden', cursor: 'pointer', minWidth: 140, flex: '0 0 auto',
      }}>
        {lane.image_url
          ? <img src={lane.image_url} alt={lane.profile_name} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block', background: '#0a0f2e' }} onError={e => { e.target.style.display = 'none' }} />
          : <div style={{ width: '100%', height: 90, background: '#0a0f2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📷</div>
        }
        <div style={{ padding: '6px 8px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lane.profile_name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{lane.license_plate || '—'}</div>
          {ago && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{ago}</div>}
        </div>
      </div>
    )
  }

  return (
    <>
      {selectedLane && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedLane(null)} style={{ alignItems: 'center' }}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{selectedLane.profile_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {selectedLane.lane_type} &middot; Plate: {selectedLane.license_plate || '—'} &middot; {selectedLane.captured_at ? new Date(selectedLane.captured_at).toLocaleString() : '—'}
                </div>
              </div>
              <button className="btn btn-sm" onClick={() => setSelectedLane(null)}>Close</button>
            </div>
            {selectedLane.image_url
              ? <img src={selectedLane.image_url} alt={selectedLane.profile_name} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)', background: '#0a0f2e' }} />
              : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No image available</div>
            }
          </div>
        </div>
      )}

      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          Last captured images
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>tap to enlarge</span>
        </div>

        {kiosks.length > 0 && (
          <div style={{ marginBottom: pos.length > 0 ? 12 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
              Kiosk lanes ({kiosks.length})
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {kiosks.map(l => <LaneCard key={l.id} lane={l} />)}
            </div>
          </div>
        )}

        {pos.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
              POS lanes ({pos.length})
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {pos.map(l => <LaneCard key={l.id} lane={l} />)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function ResourceModal({ resource, onClose }) {
  if (!resource) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{ alignItems: 'center' }}>
      <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {resource.file_type === 'image' ? 'Image' : 'Document'}: {resource.file_name}
          </div>
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        </div>
        {resource.file_type === 'image'
          ? <img src={resource.url} alt={resource.file_name} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)' }} />
          : (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>{resource.file_name}</div>
              <a href={resource.url} target="_blank" rel="noreferrer" className="btn btn-primary">Open PDF</a>
            </div>
          )
        }
      </div>
    </div>
  )
}

export default function AuditDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [audit, setAudit] = useState(null)
  const [siteDetails, setSiteDetails] = useState(null)
  const [laneImages, setLaneImages] = useState([])
  const [categories, setCategories] = useState([])
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [photos, setPhotos] = useState({})
  const [lightbox, setLightbox] = useState(null) // { urls: string[], index: number } | null
  const [resources, setResources] = useState({})
  const [activeCategory, setActiveCategory] = useState(null)
  const [auditNotes, setAuditNotes] = useState('')
  const [tab, setTab] = useState('checklist')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState({})
  const [loading, setLoading] = useState(true)
  const [showSiteInfo, setShowSiteInfo] = useState(true)
  const [activeResource, setActiveResource] = useState(null)

  const fetchAll = useCallback(async () => {
    const [auditRes, catsRes, qsRes, answersRes, resourcesRes] = await Promise.all([
      supabase.from('audits').select('*, sites(id, name, city, address)').eq('id', id).single(),
      supabase.from('categories').select('*').eq('active', true).order('sort_order'),
      supabase.from('questions').select('*').eq('active', true).order('sort_order'),
      supabase.from('audit_answers').select('*, photos(id, storage_path)').eq('audit_id', id),
      supabase.from('question_resources').select('*'),
    ])

    const auditData = auditRes.data
    setAudit(auditData)
    setAuditNotes(auditData?.notes || '')
    setCategories(catsRes.data || [])
    setQuestions(qsRes.data || [])

    // Fetch site details + lane images
    if (auditData?.sites?.id) {
      const [detRes, lanesRes] = await Promise.all([
        supabase.from('site_details').select('*').eq('site_id', auditData.sites.id).single(),
        supabase.from('lane_images').select('*').eq('site_id', auditData.sites.id).order('lane_type').order('profile_name'),
      ])
      setSiteDetails(detRes.data)
      setLaneImages(lanesRes.data || [])
    }

    // Build signed URLs for question resources
    const rMap = {}
    for (const r of (resourcesRes.data || [])) {
      const { data: urlData } = await supabase.storage.from('question-resources').createSignedUrl(r.storage_path, 3600)
      if (!rMap[r.question_id]) rMap[r.question_id] = []
      rMap[r.question_id].push({ ...r, url: urlData?.signedUrl || '' })
    }
    setResources(rMap)

    // Build answers map + photo URLs
    const answersMap = {}
    const photosMap = {}
    for (const ans of (answersRes.data || [])) {
      answersMap[ans.question_id] = { id: ans.id, result: ans.result, notes: ans.notes || '' }
      if (ans.photos?.length) {
        const urls = await Promise.all(ans.photos.map(async p => {
          const { data } = await supabase.storage.from('audit-photos').createSignedUrl(p.storage_path, 3600)
          return { id: p.id, url: data?.signedUrl || '' }
        }))
        photosMap[ans.id] = urls
      }
    }
    setAnswers(answersMap)
    setPhotos(photosMap)
    if (catsRes.data?.length) setActiveCategory(catsRes.data[0].id)
    setLoading(false)
  }, [id])

  useEffect(() => { fetchAll() }, [fetchAll])

  async function setResult(questionId, result) {
    const existing = answers[questionId]
    const newResult = existing?.result === result ? null : result
    setAnswers(prev => ({ ...prev, [questionId]: { ...prev[questionId], result: newResult } }))
    if (existing?.id) {
      await supabase.from('audit_answers').update({ result: newResult }).eq('id', existing.id)
    } else {
      const { data } = await supabase.from('audit_answers')
        .upsert({ audit_id: id, question_id: questionId, result: newResult }, { onConflict: 'audit_id,question_id' })
        .select().single()
      if (data) setAnswers(prev => ({ ...prev, [questionId]: { id: data.id, result: newResult, notes: '' } }))
    }
  }

  async function saveNotes(questionId, notes) {
    setAnswers(prev => ({ ...prev, [questionId]: { ...prev[questionId], notes } }))
    const existing = answers[questionId]
    if (existing?.id) {
      await supabase.from('audit_answers').update({ notes }).eq('id', existing.id)
    } else {
      const { data } = await supabase.from('audit_answers')
        .upsert({ audit_id: id, question_id: questionId, notes }, { onConflict: 'audit_id,question_id' })
        .select().single()
      if (data) setAnswers(prev => ({ ...prev, [questionId]: { id: data.id, result: null, notes } }))
    }
  }

  async function saveAuditNotes() {
    await supabase.from('audits').update({ notes: auditNotes }).eq('id', id)
  }

  async function uploadPhoto(questionId, file) {
    let answerId = answers[questionId]?.id
    if (!answerId) {
      const { data } = await supabase.from('audit_answers')
        .upsert({ audit_id: id, question_id: questionId }, { onConflict: 'audit_id,question_id' })
        .select().single()
      if (data) { answerId = data.id; setAnswers(prev => ({ ...prev, [questionId]: { id: data.id, result: null, notes: '' } })) }
    }
    if (!answerId) return
    setUploading(prev => ({ ...prev, [questionId]: true }))
    const ext = file.name.split('.').pop()
    const path = `${user.id}/${id}/${questionId}-${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('audit-photos').upload(path, file)
    if (!upErr) {
      await supabase.from('photos').insert({ answer_id: answerId, storage_path: path })
      const { data } = await supabase.storage.from('audit-photos').createSignedUrl(path, 3600)
      setPhotos(prev => ({ ...prev, [answerId]: [...(prev[answerId] || []), { url: data?.signedUrl || '' }] }))
    }
    setUploading(prev => ({ ...prev, [questionId]: false }))
  }

  async function submitAudit() {
    setSaving(true)
    await supabase.from('audits').update({ status: 'submitted', submitted_at: new Date().toISOString(), notes: auditNotes }).eq('id', id)
    setAudit(prev => ({ ...prev, status: 'submitted' }))
    setSaving(false)
    alert('Audit submitted successfully!')
    navigate('/audits')
  }

  function getCatProgress(catId) {
    const qs = questions.filter(q => q.category_id === catId)
    const done = qs.filter(q => answers[q.id]?.result).length
    return { total: qs.length, done, pct: qs.length ? Math.round(done / qs.length * 100) : 0 }
  }

  function getOverallProgress() {
    const done = questions.filter(q => answers[q.id]?.result).length
    return { total: questions.length, done, pct: questions.length ? Math.round(done / questions.length * 100) : 0 }
  }

  if (loading) return <div className="spinner" />
  if (!audit) return <div className="error-msg">Audit not found.</div>

  const overall = getOverallProgress()
  const activeQs = questions.filter(q => q.category_id === activeCategory)
  const isSubmitted = audit.status === 'submitted'
  const allAnswers = Object.values(answers)
  const fails = allAnswers.filter(a => a.result === 'fail')

  return (
    <div>
      {activeResource && <ResourceModal resource={activeResource} onClose={() => setActiveResource(null)} />}

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <button className="btn btn-sm" onClick={() => navigate('/audits')} style={{ marginBottom: 10 }}>Back</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700 }}>{audit.sites?.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
              {[audit.sites?.address, audit.sites?.city].filter(Boolean).join(', ')}
              {' · '}{new Date(audit.created_at).toLocaleDateString()}
            </div>
          </div>
          <span className={`badge ${isSubmitted ? 'badge-pass' : 'badge-warn'}`}>
            {isSubmitted ? 'Submitted' : 'In progress'}
          </span>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>Overall progress</span>
            <span>{overall.done}/{overall.total} items · {overall.pct}%</span>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${overall.pct}%` }} /></div>
        </div>
      </div>

      {/* Site info + lane images */}
      {(siteDetails || laneImages.length > 0) && (
        <div style={{ marginBottom: 4 }}>
          <button className="btn btn-sm" style={{ marginBottom: 8, fontSize: 12 }} onClick={() => setShowSiteInfo(p => !p)}>
            {showSiteInfo ? 'Hide site info' : 'Show site info'}
          </button>
          {showSiteInfo && (
            <>
              <SiteInfoPanel details={siteDetails} />
              <LaneImagesPanel lanes={laneImages} />
            </>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab-btn ${tab === 'checklist' ? 'active' : ''}`} onClick={() => setTab('checklist')}>Checklist</button>
        <button className={`tab-btn ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>
          Summary {fails.length > 0 && <span className="badge badge-fail" style={{ marginLeft: 4 }}>{fails.length}</span>}
        </button>
        <button className={`tab-btn ${tab === 'notes' ? 'active' : ''}`} onClick={() => setTab('notes')}>Notes</button>
      </div>

      {/* CHECKLIST TAB */}
      {tab === 'checklist' && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {categories.map(cat => {
              const prog = getCatProgress(cat.id)
              return (
                <div key={cat.id} className={`cat-card ${activeCategory === cat.id ? 'active' : ''}`} onClick={() => setActiveCategory(cat.id)}>
                  <div className="cat-icon">{cat.icon}</div>
                  <div className="cat-info">
                    <div className="cat-name">{cat.name}</div>
                    <div style={{ marginTop: 6 }}>
                      <div className="progress-bar" style={{ height: 4 }}><div className="progress-fill" style={{ width: `${prog.pct}%` }} /></div>
                    </div>
                    <div className="cat-count">{prog.done}/{prog.total}</div>
                  </div>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16, color: 'var(--text-muted)', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              )
            })}
          </div>

          {activeCategory && (
            <div className="card">
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                {categories.find(c => c.id === activeCategory)?.icon}{' '}
                {categories.find(c => c.id === activeCategory)?.name}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Tap Pass, Fail, or N/A for each item</p>
              {activeQs.map(q => {
                const ans = answers[q.id] || {}
                const answerId = ans.id
                const qPhotos = answerId ? (photos[answerId] || []) : []
                const qResources = resources[q.id] || []
                return (
                  <div key={q.id} className="check-item">
                    <div className="check-label">{q.label}</div>
                    {q.hint && <div className="check-hint">{q.hint}</div>}

                    {qResources.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0' }}>
                        {qResources.map(r => (
                          <button key={r.id} onClick={() => setActiveResource(r)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 10px', borderRadius: 6, fontSize: 12,
                            background: 'var(--blue-light)', color: 'var(--blue)',
                            border: '1px solid #b5d4f4', cursor: 'pointer', fontWeight: 500,
                          }}>
                            {r.file_type === 'image' ? 'Image' : 'PDF'}: {r.file_name}
                          </button>
                        ))}
                      </div>
                    )}

                    {!isSubmitted && (
                      <div className="result-btns">
                        {['pass', 'fail', 'na'].map(r => (
                          <button key={r} className={`result-btn ${ans.result === r ? `selected-${r}` : ''}`} onClick={() => setResult(q.id, r)}>
                            {r === 'na' ? 'N/A' : r.charAt(0).toUpperCase() + r.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}

                    {isSubmitted && ans.result && (
                      <span className={`badge badge-${ans.result}`} style={{ marginTop: 6, display: 'inline-block' }}>
                        {ans.result === 'na' ? 'N/A' : ans.result.charAt(0).toUpperCase() + ans.result.slice(1)}
                      </span>
                    )}

                    <textarea
                      className="form-input"
                      placeholder="Add notes or comments..."
                      value={ans.notes || ''}
                      onChange={e => setAnswers(prev => ({ ...prev, [q.id]: { ...prev[q.id], notes: e.target.value } }))}
                      onBlur={e => saveNotes(q.id, e.target.value)}
                      disabled={isSubmitted}
                      style={{ marginTop: 8, minHeight: 60, fontSize: 13 }}
                    />

                    {!isSubmitted && (
                      <div className="photo-grid" style={{ marginTop: 8 }}>
                        {qPhotos.map((p, i) => (
                          <img
                            key={i}
                            src={p.url}
                            alt="evidence"
                            className="photo-thumb"
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => setLightbox({ urls: qPhotos.map(x => x.url), index: i })}
                          />
                        ))}
                        <label className="photo-upload-btn">
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {uploading[q.id] ? 'Uploading...' : 'Photo'}
                          {/* No capture attribute — iOS shows Photo Library /
                              Take Photo / Choose File action sheet. With
                              capture='environment' it jumps straight to the
                              camera, which was confusing for auditors who
                              wanted to attach pre-existing photos. */}
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => e.target.files[0] && uploadPhoto(q.id, e.target.files[0])} />
                        </label>
                      </div>
                    )}
                    {isSubmitted && qPhotos.length > 0 && (
                      <div className="photo-grid" style={{ marginTop: 8 }}>
                        {qPhotos.map((p, i) => (
                          <img
                            key={i}
                            src={p.url}
                            alt="evidence"
                            className="photo-thumb"
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => setLightbox({ urls: qPhotos.map(x => x.url), index: i })}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* SUMMARY TAB */}
      {tab === 'summary' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card"><div className="stat-number" style={{ color: 'var(--green)' }}>{allAnswers.filter(a => a.result === 'pass').length}</div><div className="stat-label">Passed</div></div>
            <div className="stat-card"><div className="stat-number" style={{ color: 'var(--red)' }}>{allAnswers.filter(a => a.result === 'fail').length}</div><div className="stat-label">Failed</div></div>
            <div className="stat-card"><div className="stat-number">{allAnswers.filter(a => a.result === 'na').length}</div><div className="stat-label">N/A</div></div>
            <div className="stat-card"><div className="stat-number">{overall.pct}%</div><div className="stat-label">Complete</div></div>
          </div>
          {fails.length > 0 && (
            <div className="card" style={{ borderColor: '#f09595', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--red)', marginBottom: 12 }}>Failures requiring attention</h3>
              {fails.map(ans => {
                const qId = Object.keys(answers).find(k => answers[k] === ans)
                const q = questions.find(q => q.id === qId)
                const cat = categories.find(c => c.id === q?.category_id)
                return (
                  <div key={ans.id} className="check-item">
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{q?.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{cat?.name}</div>
                    {ans.notes && <div style={{ fontSize: 13, color: 'var(--red)', marginTop: 4 }}>{ans.notes}</div>}
                  </div>
                )
              })}
            </div>
          )}
          {allAnswers.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <h3>No answers yet</h3>
              <p>Go to the Checklist tab to start answering.</p>
            </div>
          )}
        </div>
      )}

      {/* NOTES TAB */}
      {tab === 'notes' && (
        <div className="card">
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Overall audit notes</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>General observations, follow-up actions, or anything else to note for this audit.</p>
          <textarea className="form-input" placeholder="Enter overall notes for this audit..." value={auditNotes} onChange={e => setAuditNotes(e.target.value)} onBlur={saveAuditNotes} disabled={isSubmitted} style={{ minHeight: 200 }} />
          {!isSubmitted && <button className="btn" style={{ marginTop: 10 }} onClick={saveAuditNotes}>Save notes</button>}
        </div>
      )}

      {/* Submit */}
      {!isSubmitted && (
        <div style={{ marginTop: 24, paddingBottom: 8 }}>
          <button className="btn btn-primary" style={{ width: '100%', padding: 14, fontSize: 15 }} onClick={submitAudit} disabled={saving}>
            {saving ? 'Submitting...' : `Submit Audit (${overall.done}/${overall.total} answered)`}
          </button>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>You can still edit this audit after submitting.</p>
        </div>
      )}

      {lightbox && (
        <PhotoLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}
